/**
 * Pure helpers for working with product variations and attribute options
 * returned by the `/configurations` REST endpoint.
 */

/**
 * All possible option values seen across any variation for an attribute.
 */
export function getAttributeOptions(attributeId, product, attributeOptionsMap) {
  return collectOptions(product.variations, attributeId, attributeOptionsMap);
}

/**
 * Option values that remain valid given the currently selected attribute
 * combination. Only variations matching every current selection contribute.
 */
export function getFilteredAttributeOptions(
  attributeId,
  product,
  currentSelections,
  attributeOptionsMap,
) {
  const matching = product.variations.filter((variation) =>
    Object.entries(currentSelections).every(([attrId, optionId]) => variation[attrId] == optionId),
  );

  return collectOptions(matching, attributeId, attributeOptionsMap);
}

/**
 * Find the variation index that matches the full selected attribute set,
 * or -1 if no variation matches.
 */
export function findMatchingVariantIndex(product, selectedAttributes) {
  return product.variations.findIndex((variation) =>
    Object.entries(selectedAttributes).every(([attrId, optionId]) => variation[attrId] == optionId),
  );
}

/**
 * True when an option is unavailable across the product's in-assortment
 * variations for the given attribute.
 */
export function isOptionSoldOut(product, attributeId, optionId) {
  return !product.variations.some(
    (variation) => variation[attributeId] == optionId && variation.in_assortment === true,
  );
}

function collectOptions(variations, attributeId, attributeOptionsMap) {
  const optionIds = new Set();

  variations.forEach((variation) => {
    if (variation[attributeId] !== undefined) {
      optionIds.add(variation[attributeId]);
    }
  });

  return Array.from(optionIds).map((optionId) => {
    const option = attributeOptionsMap[optionId] || {
      name: `Option ${optionId}`,
      has_display: false,
    };
    return { id: optionId, ...option };
  });
}
